import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  CollectionUtil,
  ElderDataToolbarComponent,
  ElderFormFieldDenseDirective,
  ElderQuantityInputControlComponent,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderValidationErrorDirective,
  Quantity,
  SelectionModelPopupDirective,
  Unit,
} from '@elderbyte/ngx-starter'
import { ItemSortProperty } from '../item-sort-spec'
import { Property, PropertyKind, PropertyType } from '../../../../../properties/property'
import { PropertyCache } from '../../../../../properties/property-cache'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { AsyncPipe } from '@angular/common'
import { SortOrderSelectDirective } from '../sort-order-select.directive'
import { PropertyBrowserComponent } from '../../../../../properties/property-browser/property-browser.component'
import { FormsModule } from '@angular/forms'
import { PropertySelectDirective } from '../../../../../properties/property-select/property-select.directive'
import { MatError, MatFormField } from '@angular/material/form-field'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort } from '@angular/material/sort'

@Component({
  selector: 'app-item-sort-property-table',
  templateUrl: './item-sort-property-table.component.html',
  styleUrls: ['./item-sort-property-table.component.scss'],
  providers: [PropertyCache],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    ElderFormFieldDenseDirective,
    ElderSelectComponent,
    PropertySelectDirective,
    FormsModule,
    ElderSelectValueDirective,
    SelectionModelPopupDirective,
    PropertyBrowserComponent,
    SortOrderSelectDirective,
    ElderQuantityInputControlComponent,
    MatError,
    ElderValidationErrorDirective,
    MatIconButton,
    ElderStopEventPropagationDirective,
    MatIcon,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ItemSortPropertyTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly PropertyKind = PropertyKind

  public readonly sortProperties$ = new BehaviorSubject<ItemSortProperty[]>([])

  protected readonly PropertyType = PropertyType

  @Output()
  public readonly sortPropertiesChange = new EventEmitter<ItemSortProperty[]>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {}

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get sortProperties(): ItemSortProperty[] {
    return this.sortProperties$.getValue()
  }

  @Input()
  public set sortProperties(properties: ItemSortProperty[]) {
    this.sortProperties$.next(properties)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onPropertyChange(itemSortProperty: ItemSortProperty, property: Property) {
    itemSortProperty.property = property
    if (!itemSortProperty.tolerance || itemSortProperty.tolerance.unit != property.unit) {
      itemSortProperty.tolerance = Quantity.zero(property.unit ? property.unit : Unit.cm)
    }
  }

  public addProperty(): void {
    const properties = [...this.sortProperties]
    properties.push(new ItemSortProperty())
    this.sortProperties = properties
    this.emitCurrentState()
  }

  public onRemove(toRemove: ItemSortProperty[]) {
    toRemove.forEach((r) => this.sortProperties.splice(this.sortProperties.indexOf(r), 1))
    this.sortProperties = [...this.sortProperties]
    this.emitCurrentState()
  }

  public moveUp(prop: ItemSortProperty): void {
    const properties = [...this.sortProperties]
    CollectionUtil.moveUp(properties, prop)
    this.sortProperties = properties
    this.emitCurrentState()
  }

  public moveDown(prop: ItemSortProperty): void {
    const properties = [...this.sortProperties]
    CollectionUtil.moveDown(properties, prop)
    this.sortProperties = properties
    this.emitCurrentState()
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private emitCurrentState(): void {
    this.sortPropertiesChange.emit(this.sortProperties)
  }
}
